import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import {
  convertToDateAndTime,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  resolveMetadataTableColumnsFromApiResponse,
  urlEndPoint,
} from "helpers/GeneralHelper";
import moment from "moment";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
  ALL_ROWS_PAGE_SIZE,
  buildPageSizeOptions,
  formatPageSizeLabel,
  resolvePageSizeDisplayRange,
} from "components/Common/ServerListPagination";

const FALLBACK_COLUMNS = [
  { accessor: "candidateName", Header: "Candidate" },
  { accessor: "clientName", Header: "Client" },
  { accessor: "stageName", Header: "Stage" },
  { accessor: "lastEventType", Header: "Outcome" },
  { accessor: "lastOccurredAtUtc", Header: "Last occurred (UTC)" },
];

const BASE_PAGINATION_SIZE_OPTIONS = DEFAULT_PAGE_SIZE_OPTIONS;

const extractRowsFromResponse = (response = {}) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const extractTotalRecords = (response = {}, fallbackRowsLength = 0) => {
  const payload = response?.data;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const total = Number(payload?.totalRecords);
    if (Number.isFinite(total)) return total;
  }
  return fallbackRowsLength;
};

const normalizeOutcomeModalColumns = (columns = []) =>
  (Array.isArray(columns) ? columns : []).map((column) => {
    const accessor = String(column?.accessor ?? "").trim();
    if (accessor !== "lastOccurredAtUtc") return column;
    return {
      ...column,
      Header: "Last occurred (UTC)",
      type: undefined,
    };
  });

/** Format API UTC datetime with date/time gap + AM/PM, without shifting timezone. */
const formatLastOccurredAtUtc = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";

  const parsedUtc = moment.utc(
    raw,
    [
      moment.ISO_8601,
      "YYYY-MM-DDTHH:mm:ss.SSS",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss.SSS",
      "YYYY-MM-DD HH:mm:ss",
    ],
    true
  );
  if (parsedUtc.isValid()) {
    return parsedUtc.format("MM-DD-YYYY hh:mm A");
  }

  return convertToDateAndTime(raw) || raw;
};

const resolveCellValue = (row = {}, column = {}) => {
  const accessor = String(column?.accessor ?? "").trim();
  if (!accessor) return "—";

  const value = row?.[accessor];
  if (value === null || value === undefined || value === "") return "—";

  if (accessor === "lastOccurredAtUtc") {
    return formatLastOccurredAtUtc(value);
  }

  return value;
};

const resolveCallDetailsHref = (row = {}) => {
  const candidateClientId = String(row?.candidateClientId ?? "").trim();
  return candidateClientId ? `/${urlEndPoint.callDetails}/${candidateClientId}` : "";
};

const SortIcon = ({ column, sortConfig }) => {
  const isActive = sortConfig.key === column;
  const direction = isActive ? sortConfig.direction : null;
  const activeColor = "#212529";
  const inactiveColor = "#c4c4c4";

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        lineHeight: 0,
        marginLeft: 4,
        verticalAlign: "middle",
      }}
    >
      <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
        <path d="M4 0l4 5H0z" fill={direction === "asc" ? activeColor : inactiveColor} />
      </svg>
      <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block", marginTop: 2 }}>
        <path d="M4 5L0 0h8z" fill={direction === "desc" ? activeColor : inactiveColor} />
      </svg>
    </span>
  );
};

SortIcon.propTypes = {
  column: PropTypes.string,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
};

const AiPipelineOutcomeModal = ({
  isOpen = false,
  onClose,
  loading = false,
  response = null,
  title = "Candidates",
  currentPage = 1,
  rowsPerPage = 20,
  totalPages = 0,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
}) => {
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { permissionData } = usePermissionContext() || {};
  const canShowAllRows = Boolean(permissionData?.CandidateShowAllAccess);
  const pageSizeOptions = useMemo(
    () => buildPageSizeOptions(BASE_PAGINATION_SIZE_OPTIONS, canShowAllRows),
    [canShowAllRows]
  );

  const columns = useMemo(() => {
    const fromMetadata = resolveMetadataTableColumnsFromApiResponse(response);
    return normalizeOutcomeModalColumns(
      fromMetadata.length ? fromMetadata : FALLBACK_COLUMNS
    );
  }, [response]);

  const rows = useMemo(() => extractRowsFromResponse(response), [response]);

  useEffect(() => {
    setSortConfig({ key: null, direction: "asc" });
  }, [response]);

  const handleSort = (accessor) => {
    const key = String(accessor ?? "").trim();
    if (!key) return;

    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      if (aValue === null || aValue === undefined || aValue === "") return 1;
      if (bValue === null || bValue === undefined || bValue === "") return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rows, sortConfig]);

  const resolvedTotalRecords = useMemo(
    () => extractTotalRecords(response, rows.length),
    [response, rows.length]
  );
  const resolvedTotalPages = useMemo(() => {
    const fromProp = Number(totalPages);
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
    if (Number(rowsPerPage) === ALL_ROWS_PAGE_SIZE) return 1;
    const size = Number(rowsPerPage) || DEFAULT_PAGE_SIZE;
    const total = Number(totalRecords) || resolvedTotalRecords;
    return total > 0 ? Math.ceil(total / size) : 0;
  }, [totalPages, rowsPerPage, totalRecords, resolvedTotalRecords]);

  const displayTotalRecords = Number(totalRecords) || resolvedTotalRecords;
  const { displayFrom, displayTo } = resolvePageSizeDisplayRange(
    currentPage,
    rowsPerPage,
    displayTotalRecords
  );
  const showPagination = typeof onPageChange === "function";

  const handlePrevPage = () => {
    if (currentPage <= 1 || loading) return;
    onPageChange?.(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage >= resolvedTotalPages || loading) return;
    onPageChange?.(currentPage + 1);
  };

  const handleRowsPerPageChange = (nextPageSize) => {
    if (loading) return;
    onPageSizeChange?.(nextPageSize);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      backdrop="static"
      centered
      size="lg"
      zIndex={5000}
      className="ai-pipeline-outcome-modal modal-full-height"
    >
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3 gap-3">
          <div>
            <h5 className="fw-bold mb-1">{title}</h5>
            {!loading && response ? (
              <p className="text-muted mb-0 small">
                {displayTotalRecords} candidate{displayTotalRecords === 1 ? "" : "s"}
              </p>
            ) : null}
          </div>
          <Button close type="button" onClick={onClose} aria-label="Close" />
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" className="me-2" />
            Loading candidates…
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table bordered className="align-middle mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "64px" }}>
                      Sl No
                    </th>
                    {columns.map((column) => {
                      const accessor = String(column?.accessor ?? "").trim();
                      return (
                        <th
                          key={accessor || column?.Header}
                          onClick={() => handleSort(accessor)}
                          style={{ cursor: accessor ? "pointer" : "default", userSelect: "none" }}
                        >
                          {column?.Header || accessor}
                          {accessor ? <SortIcon column={accessor} sortConfig={sortConfig} /> : null}
                        </th>
                      );
                    })}
                    <th className="text-center" style={{ width: "72px" }}>
                      
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length ? (
                    sortedRows.map((row, index) => {
                      const rowKey =
                        row?.candidateClientId ??
                        row?.candidateId ??
                        row?.id ??
                        `${row?.clientId || "row"}-${index}`;
                      const detailsHref = resolveCallDetailsHref(row);
                      const callActionId = `ai-pipeline-call-details-${rowKey}`;
                      const serialNo = (currentPage - 1) * rowsPerPage + index + 1;

                      return (
                        <tr key={rowKey}>
                          <td className="text-center">{serialNo}</td>
                          {columns.map((column) => {
                            const accessor = String(column?.accessor ?? "").trim();
                            const displayValue = resolveCellValue(row, column);

                            return <td key={accessor || column?.Header}>{displayValue}</td>;
                          })}
                          <td className="text-center">
                            {detailsHref ? (
                              <div className="d-inline-flex align-items-center justify-content-center">
                                <Link
                                  to={detailsHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                  id={callActionId}
                                  aria-label="Call details"
                                >
                                  <i className="mdi mdi-phone-log text-success" aria-hidden />
                                </Link>
                                <UncontrolledTooltip target={callActionId}>
                                  Call details
                                </UncontrolledTooltip>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={(columns.length || 1) + 2}
                        className="text-center text-muted py-4"
                      >
                        No candidates found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {showPagination ? (
              <div className="d-flex justify-content-end align-items-center px-1 py-2 border-top bg-white mt-2">
                <div className="d-flex align-items-center gap-2 me-3">
                  <span className="text-muted small">Rows per page:</span>
                  <Dropdown
                    isOpen={pageSizeDropdownOpen}
                    toggle={() => setPageSizeDropdownOpen((previous) => !previous)}
                  >
                    <DropdownToggle caret color="light" size="sm" className="border bg-white">
                      {formatPageSizeLabel(rowsPerPage)}
                    </DropdownToggle>
                    <DropdownMenu>
                      {pageSizeOptions.map((num) => (
                        <DropdownItem
                          key={num}
                          onClick={() => handleRowsPerPageChange(num)}
                        >
                          {formatPageSizeLabel(num)}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
                <span className="text-muted small me-3">
                  {displayFrom}–{displayTo} of {displayTotalRecords}
                </span>
                <div className="d-flex align-items-center gap-1">
                  <Button
                    color="link"
                    className="text-muted p-0"
                    disabled={currentPage <= 1 || loading}
                    onClick={handlePrevPage}
                  >
                    <svg width="18" height="18" fill="currentColor">
                      <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                    </svg>
                  </Button>
                  <Button
                    color="link"
                    className="text-muted p-0"
                    disabled={currentPage >= resolvedTotalPages || loading || !resolvedTotalPages}
                    onClick={handleNextPage}
                  >
                    <svg width="18" height="18" fill="currentColor">
                      <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                    </svg>
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

AiPipelineOutcomeModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  response: PropTypes.object,
  title: PropTypes.string,
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalRecords: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

export default AiPipelineOutcomeModal;
